import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import type { EarningType } from 'Deer/pages/Company/Compensation/utils/payUtils';

import type { GuidelinesRow, GuidelineType } from '../../dataHooks';
import type { BUDGET_TIMELINE_TYPE } from '../BudgetTimeline';

// Auto-save status constants - mirrors the Reviews module pattern
export const AUTO_UPDATE_STATUSES = {
  IDLE: 'IDLE',
  IS_SAVING: 'IS_SAVING',
  UPDATED: 'UPDATED',
  IS_ERROR: 'IS_ERROR',
} as const;

export type AutoUpdateStatus = (typeof AUTO_UPDATE_STATUSES)[keyof typeof AUTO_UPDATE_STATUSES];

// Type definitions for guideline data structure
export interface GuidelineDetail {
  label: string;
  reviewScore: number;
  minValue: number | null;
  maxValue: number | null;
}

// Employee/Function data structure for hierarchical displays
interface FunctionData {
  displayName: string;
  subtitle?: string;
  designation?: string;
  profilePhoto?: string;
  isEmployee?: boolean;
  isGroup?: boolean;
  level: 'ORG' | 'COMPANY' | 'DIVISION' | 'BU' | 'DEPARTMENT' | 'EMPLOYEE';
  displayLevel?: string | null;
  isHighlighted?: boolean;
}

// Base stakeholder row structure
interface BaseStakeholderRow {
  id: number;
  NOTE_ID?: string | number;
  [columnId: string]: unknown;
}

// Flat stakeholder row (used in StakeHolderFlatListTable)
export interface StakeholderRow extends BaseStakeholderRow {
  RAISED_FOR?: unknown;
  PROPOSER?: string;
  APPROVER?: string;
  PUBLISHER?: string;
  approverSelectedAdminEmployees?: SelectedEmployee[];
  approverSelectedManagerEmployees?: SelectedEmployee[];
  proposerSelectedAdminEmployees?: SelectedEmployee[];
  proposerSelectedManagerEmployees?: SelectedEmployee[];
  publisherSelectedAdminEmployees?: SelectedEmployee[];
  publisherSelectedManagerEmployees?: SelectedEmployee[];
}

export interface SelectedEmployee {
  id: number;
  value: number;
  label: string;
  profilePhoto: string;
}

// Propose table row type definitions
export interface ProposeTableGuidelineInfo {
  minValue: number;
  maxValue: number;
  isGuidelineEnforced?: boolean; // true = mandatory (breach not allowed), false = optional (breach allowed), undefined = no guideline
}

export interface ProposeTableEmployeeMetadata {
  createdTS: number;
  updatedTS: number;
  externalEmployeeId: string;
  firstName: string;
  lastName: string;
  displayName: string;
  designation: string;
  location: string;
  gender: string;
  joiningDate: string;
  resignationDate: string | null;
  department: string;
  tenure: string;
  email: string;
  country: string;
  preferredFirstName: string | null;
  dateOfBirth: string | null;
  managerEmail: string;
  ethnicity: string | null;
  jobFunctionId: number;
  jobRoleId: number;
  employeeFunction: string;
  role: string;
  jobGradeUI: string | null;
  jobGradeNameUI: string | null;
  preferredName: string;
  customFields: Record<string, unknown>;
  experienceBeforeJoining: number;
  companyId: number;
  personalEmail: string;
  workEmail: string;
  workPhoneNumber: string | null;
  mobilePhoneNumber: string | null;
  homePhoneNumber: string | null;
  netchexEmployeeCode: string | null;
}

export interface ProposeTableEmployeeProfile {
  createdTS: number;
  updatedTS: number;
  profilePhoto: string;
  lastNotified: string | null;
  lastActiveAt: string | null;
}

export interface ProposeTableEmployee {
  active: boolean;
  uuid: string;
  createdTS: number;
  updatedTS: number;
  organizationId: number;
  metadata: ProposeTableEmployeeMetadata;
  profile: ProposeTableEmployeeProfile;
  userRole: string;
  signUpCompletionTS: number;
  lastUserEvent: string;
  timezoneOffsetDiff: number;
  employeeAccesses: string[];
  customAccessDefinitionIds: unknown[];
  firstLoginTS: number;
  canCreateSharedCohort: boolean;
  timezone: string;
  isTestUser: boolean;
  signUpCompletionPending: boolean;
  netchexAdmin: boolean;
  dummyDataRemoved: boolean;
  validEmployee: boolean;
  organizationUuid: string;
}

export interface ProposeTableCompensationStatusEmployee {
  id: number;
  companyId: number | null;
  displayName: string;
  profilePhoto: string;
  designation: string;
  ownerType: string | null;
  employeeFunction: string;
  email: string;
  personalEmail: string;
  workEmail: string;
  preferredName: string;
}

export interface ProposeTableCompensationStatus {
  currentStatus: string;
  pendingOn: string;
  deniedBy: string | null;
  employees: ProposeTableCompensationStatusEmployee[];
}

export interface ProposeTableReviewRating {
  ratingType: string;
  ratingText: string;
  isCalibrated: boolean;
}

export interface PayInfo {
  hourlyRate: number | null;
  annualSalary: number | null;
}

interface BudgetDetails {
  totalCurrentSalary: number | null;
  totalNewSalary: number | null;
  totalBudget: number | null;
}

export interface PlanningTableRow {
  BUDGET_DETAILS: BudgetDetails | null;
  EFFECTIVE_DATE: string;
  GUIDELINE_INFO: ProposeTableGuidelineInfo;
  EMPLOYEE: Employee;
  EMPLOYEE_FUNCTION: string;
  EMPLOYEE_LOCATION: string;
  // New pay structure
  CURRENT_PAY?: PayInfo;
  PROPOSED_PAY?: PayInfo;
  APPROVED_PAY?: PayInfo;
  FINAL_PAY?: PayInfo;
  // Legacy fields for backward compatibility
  CURRENT_RATE?: number;
  PROPOSED_RATE?: number;
  APPROVED_RATE?: number;
  FINAL_RATE?: number;
  COMPENSATION_STATUS: ProposeTableCompensationStatus;
  RATE_KEY: string;
  RATE_LABEL: string;
  REVIEW_RATING: ProposeTableReviewRating;
  SORT_KEY: string;
  ID: string;
  COMPENSATION_CYCLE: string;
  HAS_ADDITIONAL_RATES: boolean;
  HIERARCHY: string[];
  COMPENSATION_CYCLE_ID: number;
  EMPLOYEE_ID: number;
  EARNING_TYPE?: EarningType | null;
  ANOMALY_DETAILS?: {
    minThreshold: number;
    maxThreshold: number;
  } | null;
}

// Tree stakeholder row (used in StakeHolderTreeTable)
export interface StakeholderTreeRow extends BaseStakeholderRow {
  function: FunctionData;
  HIERARCHY: string[];
  PROPOSER: string;
  APPROVER: string;
  PUBLISHER: string;
  publisherSelectedEmployees?: SelectedEmployee[];
  proposerSelectedEmployees?: SelectedEmployee[];
  approverSelectedEmployees?: SelectedEmployee[];
  publisherSelectedAdminEmployees?: SelectedEmployee[];
  publisherSelectedManagerEmployees?: SelectedEmployee[];
  proposerSelectedAdminEmployees?: SelectedEmployee[];
  proposerSelectedManagerEmployees?: SelectedEmployee[];
  approverSelectedAdminEmployees?: SelectedEmployee[];
  approverSelectedManagerEmployees?: SelectedEmployee[];
}

// Current pay details row (used in CurrentPayDetailsTreeTable)
export interface CurrentPayDetailsRow {
  ID: string;
  function: FunctionData;
  HIERARCHY: string[];
  currentPayroll: number | string;
  percentageIncrease: number | string;
  CHANGE_PERCENTAGE: number | string;
  increasedBudget?: number;
  totalNewBudget: number;
}

// Employee data structure for approver allocation
export interface Employee {
  id: number;
  companyId?: number | null;
  displayName: string;
  profilePhoto: string;
  designation?: string | null;
  ownerType: 'OWNER' | 'COLLABORATOR';
  employeeFunction?: string | null;
  email: string;
  personalEmail?: string | null;
  workEmail?: string | null;
  preferredName?: string | null;
}

// Approver wise allocation row (used in ApproverWiseAllocationTable)
export interface ApproverWiseAllocationRow extends BaseStakeholderRow {
  RAISED_FOR?: unknown;
  EMPLOYEES?: Employee[];
  APPROVER: Employee;
  employees?: Employee[];
  currentPayroll: number;
  CURRENT_PAYROLL: string;
  ID: number;
  increasedAllocation: number;
  INCREASED_ALLOCATION: string;
  totalAllocation: number;
  adjustedBudget?: number;
  BUDGET_ADJUSTMENT: string;
}

export type CompensationCycle = {
  id: number | null;
  name: string;
  hasBudget: boolean;
};

// Updated types for the slice
interface CompensationDrawersState {
  isOpen: boolean;
  originalStakeholdersRows: StakeholderRow[];
  modifiedStakeholdersRows: StakeholderRow[];
  originalStakeholdersTreeRows: StakeholderTreeRow[];
  modifiedStakeholdersTreeRows: StakeholderTreeRow[];
  originalCurrentPayDetailsRows: CurrentPayDetailsRow[];
  modifiedCurrentPayDetailsRows: CurrentPayDetailsRow[];
  originalApproverWiseAllocationRows: ApproverWiseAllocationRow[];
  modifiedApproverWiseAllocationRows: ApproverWiseAllocationRow[];
  budgetTimelineStatus: BUDGET_TIMELINE_TYPE;
  isBudgetDrawerOpen: boolean;
  // Separate states for each guideline type
  originalCreateGuidelineRowsNoSegmentation: GuidelinesRow[];
  modifiedCreateGuidelineRowsNoSegmentation: GuidelinesRow[];
  originalCreateGuidelineRowsPerformanceLinked: GuidelinesRow[];
  modifiedCreateGuidelineRowsPerformanceLinked: GuidelinesRow[];
  isCreateGuidelineDrawerOpen: boolean;
  isFlaggedProposalsFilterApplied: boolean;
  isCompensationRecommendationBottomSheetOpen: boolean;
  guidelineType: GuidelineType;
  forceShowErrors: boolean;
  originalPlanningTableRows: PlanningTableRow[];
  modifiedPlanningTableRows: PlanningTableRow[];
  originalPlanningTableDetailsRows: { number: PlanningTableRow[] };
  modifiedPlanningTableDetailsRows: { number: PlanningTableRow[] };
  selectedCompensationCycle: CompensationCycle;
  allLocations: string[];
  selectedLocations: string[];
  selectedOrgNodes: string[];
  publishPlanningMetricData: Record<string, { isFilterActive: boolean }>;
  // Auto-save status for compensation rate mutations
  autoSaveStatus: AutoUpdateStatus;
}

export interface CompensationRootState {
  compensationDrawers: CompensationDrawersState;
}

const initialState: CompensationDrawersState = {
  isOpen: false,
  originalStakeholdersRows: [],
  modifiedStakeholdersRows: [],
  originalStakeholdersTreeRows: [],
  modifiedStakeholdersTreeRows: [],
  originalCurrentPayDetailsRows: [],
  modifiedCurrentPayDetailsRows: [],
  originalApproverWiseAllocationRows: [],
  modifiedApproverWiseAllocationRows: [],
  budgetTimelineStatus: null,
  isBudgetDrawerOpen: false,
  // Separate initial states for each guideline type
  originalCreateGuidelineRowsNoSegmentation: [],
  modifiedCreateGuidelineRowsNoSegmentation: [],
  originalCreateGuidelineRowsPerformanceLinked: [],
  modifiedCreateGuidelineRowsPerformanceLinked: [],
  isCreateGuidelineDrawerOpen: false,
  isFlaggedProposalsFilterApplied: false,
  isCompensationRecommendationBottomSheetOpen: false,
  guidelineType: 'PERFORMANCE_LINKED',
  forceShowErrors: false,
  originalPlanningTableRows: [],
  modifiedPlanningTableRows: [],
  originalPlanningTableDetailsRows: { number: [] },
  modifiedPlanningTableDetailsRows: { number: [] },
  // removing All cycles for V0 , putting empty string object for backward compatibility
  selectedCompensationCycle: { id: null, name: '', hasBudget: false },
  allLocations: [],
  selectedLocations: [],
  selectedOrgNodes: [],
  publishPlanningMetricData: {
    pendingOnProposer: {
      isFilterActive: false,
    },
    pendingOnApprover: {
      isFilterActive: false,
    },
    pendingOnPublisher: {
      isFilterActive: false,
    },
    outsideGuideline: {
      isFilterActive: false,
    },
  },
  // Default to IDLE - indicator won't show until first save
  autoSaveStatus: AUTO_UPDATE_STATUSES.IDLE,
};

export const compensationDrawersSlice = createSlice({
  name: 'compensationDrawers',
  initialState,
  reducers: {
    openWorkflowStakeholdersDrawer: (state) => {
      state.isOpen = true;
    },
    closeWorkflowStakeholdersDrawer: (state) => {
      state.isOpen = false;
    },
    toggleWorkflowStakeholdersDrawer: (state) => {
      state.isOpen = !state.isOpen;
    },
    setOriginalStakeholdersRows: (state, action) => {
      state.originalStakeholdersRows = action.payload;
    },
    setModifiedStakeholdersRows: (state, action) => {
      state.modifiedStakeholdersRows = action.payload;
    },
    setOriginalStakeholdersTreeRows: (state, action) => {
      state.originalStakeholdersTreeRows = action.payload;
    },
    setModifiedStakeholdersTreeRows: (state, action) => {
      state.modifiedStakeholdersTreeRows = action.payload;
    },
    setOriginalCurrentPayDetailsRows: (state, action) => {
      state.originalCurrentPayDetailsRows = action.payload;
    },
    setModifiedCurrentPayDetailsRows: (state, action) => {
      state.modifiedCurrentPayDetailsRows = action.payload;
    },
    setOriginalApproverWiseAllocationRows: (state, action) => {
      state.originalApproverWiseAllocationRows = action.payload;
    },
    setModifiedApproverWiseAllocationRows: (state, action) => {
      state.modifiedApproverWiseAllocationRows = action.payload;
    },
    setBudgetTimelineStatus: (state, action) => {
      state.budgetTimelineStatus = action.payload;
    },
    setIsBudgetDrawerOpen: (state, action) => {
      state.isBudgetDrawerOpen = action.payload;
    },
    setIsCreateGuidelineDrawerOpen: (state, action) => {
      state.isCreateGuidelineDrawerOpen = action.payload;
    },
    // No Segmentation actions
    setOriginalCreateGuidelineRowsNoSegmentation: (state, action) => {
      state.originalCreateGuidelineRowsNoSegmentation = action.payload;
    },
    setModifiedCreateGuidelineRowsNoSegmentation: (state, action) => {
      state.modifiedCreateGuidelineRowsNoSegmentation = action.payload;
    },
    // Performance Linked actions
    setOriginalCreateGuidelineRowsPerformanceLinked: (state, action) => {
      state.originalCreateGuidelineRowsPerformanceLinked = action.payload;
    },
    setModifiedCreateGuidelineRowsPerformanceLinked: (state, action) => {
      state.modifiedCreateGuidelineRowsPerformanceLinked = action.payload;
    },
    setIsFlaggedProposalsFilterApplied: (state, action) => {
      state.isFlaggedProposalsFilterApplied = action.payload;
    },

    setIsCompensationRecommendationBottomSheetOpen: (state, action) => {
      state.isCompensationRecommendationBottomSheetOpen = action.payload;
    },
    setOriginalPlanningTableRows: (state, action) => {
      state.originalPlanningTableRows = action.payload;
    },
    setModifiedPlanningTableRows: (state, action) => {
      state.modifiedPlanningTableRows = action.payload;
    },
    setOriginalPlanningTableDetailsRows: (state, action) => {
      state.originalPlanningTableDetailsRows = action.payload;
    },
    setModifiedPlanningTableDetailsRows: (state, action) => {
      state.modifiedPlanningTableDetailsRows = action.payload;
    },
    setGuidelineType: (state, action) => {
      state.guidelineType = action.payload;
      state.forceShowErrors = false;
      // Clear both guideline data states when switching types
      state.originalCreateGuidelineRowsNoSegmentation = [];
      state.modifiedCreateGuidelineRowsNoSegmentation = [];
      state.originalCreateGuidelineRowsPerformanceLinked = [];
      state.modifiedCreateGuidelineRowsPerformanceLinked = [];
    },
    setForceShowErrors: (state, action) => {
      state.forceShowErrors = action.payload;
    },
    setSelectedCompensationCycle: (state, action) => {
      state.selectedCompensationCycle = action.payload;
    },
    setCompensationAllLocations: (state, action) => {
      state.allLocations = action.payload;
    },
    setCompensationSelectedLocations: (state, action) => {
      state.selectedLocations = action.payload;
    },
    setCompensationSelectedOrgNodes: (state, action) => {
      state.selectedOrgNodes = action.payload;
    },
    setPublishPlanningMetricData: (state, action) => {
      state.publishPlanningMetricData = action.payload;
    },
    setAutoSaveStatus: (state, action: PayloadAction<AutoUpdateStatus>) => {
      state.autoSaveStatus = action.payload;
    },
  },
});

export const {
  openWorkflowStakeholdersDrawer,
  closeWorkflowStakeholdersDrawer,
  toggleWorkflowStakeholdersDrawer,
  setOriginalStakeholdersRows,
  setModifiedStakeholdersRows,
  setOriginalStakeholdersTreeRows,
  setModifiedStakeholdersTreeRows,
  setOriginalCurrentPayDetailsRows,
  setModifiedCurrentPayDetailsRows,
  setOriginalApproverWiseAllocationRows,
  setModifiedApproverWiseAllocationRows,
  setBudgetTimelineStatus,
  setIsBudgetDrawerOpen,
  setIsCreateGuidelineDrawerOpen,
  // No Segmentation actions
  setOriginalCreateGuidelineRowsNoSegmentation,
  setModifiedCreateGuidelineRowsNoSegmentation,
  // Performance Linked actions
  setOriginalCreateGuidelineRowsPerformanceLinked,
  setModifiedCreateGuidelineRowsPerformanceLinked,
  setIsFlaggedProposalsFilterApplied,
  setIsCompensationRecommendationBottomSheetOpen,
  setGuidelineType,
  setForceShowErrors,
  setOriginalPlanningTableRows,
  setModifiedPlanningTableRows,
  setOriginalPlanningTableDetailsRows,
  setModifiedPlanningTableDetailsRows,
  setSelectedCompensationCycle,
  setCompensationAllLocations,
  setCompensationSelectedLocations,
  setCompensationSelectedOrgNodes,
  setPublishPlanningMetricData,
  setAutoSaveStatus,
} = compensationDrawersSlice.actions;

export const useCompensationDrawersState = () =>
  useSelector((state: CompensationRootState) => state.compensationDrawers);

export const useSelectCompensationAllLocations = () =>
  useSelector((state: { compensationDrawers: typeof initialState }) => state.compensationDrawers.allLocations);

export const useSelectCompensationSelectedLocations = () =>
  useSelector((state: { compensationDrawers: typeof initialState }) => state.compensationDrawers.selectedLocations);

export const useSelectCompensationSelectedOrgNodes = () =>
  useSelector((state: { compensationDrawers: typeof initialState }) => state.compensationDrawers.selectedOrgNodes);

export const useSelectPublishPlanningMetricData = () =>
  useSelector(
    (state: { compensationDrawers: typeof initialState }) => state.compensationDrawers.publishPlanningMetricData
  );

export const useSelectSelectedCompensationCycle = () =>
  useSelector((state: CompensationRootState) => state.compensationDrawers.selectedCompensationCycle);

export const useSelectAutoSaveStatus = () =>
  useSelector((state: CompensationRootState) => state.compensationDrawers.autoSaveStatus);

export default compensationDrawersSlice.reducer;
